import { useState, useRef, useEffect, type KeyboardEvent } from 'react';
import { Icon } from './Icon';
import { loadVerbosity, saveVerbosity, VERBOSITY_VALUES, type Verbosity } from '../state/verbosity';

interface ComposerProps {
  onSubmit: (query: string) => void;
  onAbort?: () => void;
  inFlight?: boolean;
  /** Text to pre-populate the textarea with (e.g. when a saved prompt is clicked). */
  prefill?: string | null;
  /** Called after the prefill has been applied so the parent can clear its state. */
  onPrefillConsumed?: () => void;
}

export function Composer({ onSubmit, onAbort, inFlight, prefill, onPrefillConsumed }: ComposerProps) {
  const [value, setValue] = useState('');
  const [verbosity, setVerbosity] = useState<Verbosity>(loadVerbosity());
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Prefill comes from clicking a saved-prompt card / sidebar row. Drop the
  // text into the textarea, focus, place the caret at end, then tell the
  // parent to clear its prefill state so subsequent clicks re-trigger.
  useEffect(() => {
    if (prefill == null) return;
    setValue(prefill);
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) {
        ta.focus();
        ta.setSelectionRange(prefill.length, prefill.length);
      }
    });
    onPrefillConsumed?.();
  }, [prefill, onPrefillConsumed]);

  // Auto-grow up to ~140px to match Phase 1's behavior. Toggle
  // overflow-y explicitly because Safari (desktop + iOS) renders an
  // always-on scrollbar when scrollHeight is within ~1-2px of the set
  // height — even when content fits. Hide the scrollbar while content
  // fits inside the cap; show it only when we're actually clipping.
  const MAX_HEIGHT = 140;
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const contentHeight = ta.scrollHeight;
    ta.style.height = Math.min(contentHeight, MAX_HEIGHT) + 'px';
    ta.style.overflowY = contentHeight > MAX_HEIGHT ? 'auto' : 'hidden';
  }, [value]);

  // When a question finishes streaming the textarea was disabled and lost
  // focus — restore it so the user can immediately type the next question
  // without clicking back into the box.
  const prevInFlightRef = useRef(inFlight);
  useEffect(() => {
    if (prevInFlightRef.current && !inFlight) {
      textareaRef.current?.focus();
    }
    prevInFlightRef.current = inFlight;
  }, [inFlight]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || inFlight) return;
    onSubmit(trimmed);
    setValue('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const onVerbosityChange = (v: Verbosity) => {
    setVerbosity(v);
    saveVerbosity(v);
  };

  return (
    <form
      id="input-form"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className="border-t border-border px-3 sm:px-4 pt-3 sm:pt-4"
      style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
    >
      {/* The form's border stays full-width so the bottom of the chat pane
          feels anchored on a wide monitor — only the inner controls
          (textarea, footnote, detail picker) are constrained to the
          comfortable reading column that matches the assistant prose. */}
      <div className="composer-inner">
      <div className="relative flex">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask Kairos a question…"
          disabled={inFlight}
          rows={1}
          required
          className="block w-full pl-4 pr-10 py-3 border border-border rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-primary text-text bg-surface"
        />
        {inFlight ? (
          <button
            type="button"
            onClick={onAbort}
            aria-label="Stop"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-danger hover:bg-danger-deep text-white rounded-full transition-colors"
          >
            {/* Inlined instead of <Icon name="i-close"> — the shared Icon
                wrapper renders the symbol via <use href="/icons.svg#i-close">
                and iOS Safari has cascade quirks where attributes on the
                consumer <svg> don't always propagate through <use> to the
                referenced symbol. Same root cause as the previous i-stop
                fix (commit c0bc62a): the cross stayed invisible on iPhone
                even after bumping stroke-width and lengthening the lines.
                Bypassing the sprite with an inline svg is the reliable fix. */}
            <svg
              width={14}
              height={14}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={3}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="5" y1="5" x2="19" y2="19" />
              <line x1="5" y1="19" x2="19" y2="5" />
            </svg>
          </button>
        ) : (
          <button
            type="submit"
            disabled={!value.trim()}
            aria-label="Send"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-success hover:bg-success-deep text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Icon name="i-arrow-up" size={14} />
          </button>
        )}
      </div>
      <div className="flex items-center justify-between text-xs text-text-muted mt-2 flex-wrap gap-2">
        <span>Kairos may make mistakes. Verify sources.</span>
        <span className="flex items-center gap-3">
          <label className="flex items-center gap-1">
            <span>Detail:</span>
            <select
              value={verbosity}
              onChange={(e) => onVerbosityChange(e.target.value as Verbosity)}
              aria-label="Response detail level"
              className="border border-border rounded px-1 py-0.5 bg-surface text-text"
            >
              {VERBOSITY_VALUES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <span className="hidden sm:inline">
            <kbd className="px-1 border border-border rounded">↵</kbd> to send ·{' '}
            <kbd className="px-1 border border-border rounded">⇧↵</kbd> for newline
          </span>
        </span>
      </div>
      </div>
    </form>
  );
}
